import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Wallet, ArrowUpCircle, ArrowDownCircle, Loader, RefreshCw } from 'lucide-react';
import apiService from '../../apiService';

const SOURCE_LABELS = {
  topup: 'Wallet Top-up',
  refund: 'Order Refund',
  reward: 'Reward',
  cashback: 'Cashback',
  order_payment: 'Order Payment',
  admin_credit: 'Admin Credit',
  admin_debit: 'Admin Debit',
};

const WalletTransactionsModal = ({ isOpen, onClose, walletBalance }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');

  const loadTransactions = useCallback(async (pageNum = 1, append = false) => {
    setLoading(true);
    setError('');
    try {
      const resp = await apiService.getWalletTransactions(pageNum);
      if (resp?.success) {
        const data = resp.data?.transactions ?? resp.data ?? {};
        const items = data.data ?? [];
        setTransactions(prev => append ? [...prev, ...items] : items);
        setHasMore((data.current_page ?? pageNum) < (data.last_page ?? 1));
        setPage(data.current_page ?? pageNum);
      } else {
        setError(resp?.data?.message || 'Failed to load transactions');
      }
    } catch {
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTransactions([]);
      setPage(1);
      loadTransactions(1, false);
    }
  }, [isOpen, loadTransactions]);

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const totalCredits = transactions
    .filter(t => t.type === 'credit')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
  const totalDebits = transactions
    .filter(t => t.type === 'debit')
    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

  return (
    <div className="fixed inset-0 z-[300] bg-gray-50 flex flex-col" style={{ maxWidth: 448, margin: '0 auto' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 pt-5 pb-4 flex-shrink-0">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={onClose}
            className="p-2 -ml-1 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft size={22} className="text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-800">Wallet Transactions</h1>
          <button
            onClick={() => loadTransactions(1, false)}
            disabled={loading}
            className="ml-auto p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-40"
          >
            <RefreshCw size={18} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Balance + stats row */}
        <div className="flex gap-3">
          <div className="flex-1 bg-orange-50 rounded-xl p-3 border border-orange-100">
            <p className="text-xs text-orange-500 font-medium mb-0.5">Available Balance</p>
            <p className="text-xl font-bold text-orange-700">
              ₹{typeof walletBalance === 'number' ? walletBalance.toFixed(2) : '0.00'}
            </p>
          </div>
          <div className="flex-1 bg-green-50 rounded-xl p-3 border border-green-100">
            <p className="text-xs text-green-600 font-medium mb-0.5">Credits</p>
            <p className="text-lg font-bold text-green-700">+₹{totalCredits.toFixed(2)}</p>
          </div>
          <div className="flex-1 bg-red-50 rounded-xl p-3 border border-red-100">
            <p className="text-xs text-red-500 font-medium mb-0.5">Debits</p>
            <p className="text-lg font-bold text-red-600">-₹{totalDebits.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Transactions list */}
      <div className="flex-1 overflow-y-auto">
        {loading && transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 py-20">
            <Loader size={32} className="text-orange-500 animate-spin" />
            <p className="text-gray-500 text-sm">Loading transactions...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 py-20 px-6">
            <Wallet size={44} className="text-gray-300" />
            <p className="text-red-500 text-sm text-center">{error}</p>
            <button
              onClick={() => loadTransactions(1, false)}
              className="text-orange-600 text-sm font-medium underline"
            >
              Try again
            </button>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 py-20">
            <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center">
              <Wallet size={32} className="text-orange-300" />
            </div>
            <p className="text-gray-700 font-semibold">No transactions yet</p>
            <p className="text-gray-400 text-sm">Top up your wallet to get started</p>
          </div>
        ) : (
          <div className="px-4 py-3 space-y-2 pb-8">
            {transactions.map((txn) => {
              const isCredit = txn.type === 'credit';
              const label = SOURCE_LABELS[txn.source] ?? txn.source_label ?? txn.source ?? 'Transaction';
              return (
                <div
                  key={txn.id}
                  className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm border border-gray-100"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCredit ? 'bg-green-100' : 'bg-red-100'}`}>
                    {isCredit
                      ? <ArrowUpCircle size={22} className="text-green-600" />
                      : <ArrowDownCircle size={22} className="text-red-500" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{label}</p>
                    {txn.description && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">{txn.description}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">{formatDate(txn.created_at)}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className={`font-bold text-base ${isCredit ? 'text-green-600' : 'text-red-500'}`}>
                      {isCredit ? '+' : '-'}₹{parseFloat(txn.amount || 0).toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Bal: ₹{parseFloat(txn.balance_after || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              );
            })}

            {hasMore && (
              <button
                onClick={() => loadTransactions(page + 1, true)}
                disabled={loading}
                className="w-full py-3 text-orange-600 text-sm font-semibold border border-orange-200 rounded-xl bg-white hover:bg-orange-50 disabled:opacity-50 transition-colors mt-2"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader size={16} className="animate-spin" /> Loading...
                  </span>
                ) : 'Load More'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletTransactionsModal;
